import React, { useRef, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX, 
  Heart,
  Share,
  Download,
  Repeat,
  Shuffle
} from 'lucide-react'
import { useMusic } from '../contexts/MusicContext'

const MusicPlayer = ({ track, onNext, onPrevious }) => {
  const audioRef = useRef(null)
  const progressRef = useRef(null)
  
  const { 
    isPlaying, 
    currentTime, 
    duration, 
    volume,
    dispatch,
    play,
    pause,
    togglePlay,
    isFavorite,
    addFavorite,
    removeFavorite
  } = useMusic()
  
  const [isShuffled, setIsShuffled] = useState(false)
  const [isRepeated, setIsRepeated] = useState(false)
  const [isDragging, setIsDragging] = useState(false)

  // 音频事件监听
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => {
      if (!isDragging) {
        dispatch({ type: 'SET_CURRENT_TIME', payload: audio.currentTime })
      }
    }

    const handleDurationChange = () => {
      dispatch({ type: 'SET_DURATION', payload: audio.duration || 0 })
    }

    const handleEnded = () => {
      if (isRepeated) {
        audio.currentTime = 0
        audio.play()
      } else {
        onNext()
      }
    }

    const handlePlay = () => play()
    const handlePause = () => pause()

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
    }
  }, [isRepeated, onNext])

  // 播放状态控制
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      console.log('尝试播放音频:', audio.src)
      const playPromise = audio.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('音频播放成功')
          })
          .catch(error => {
            console.error('音频播放失败:', error)
            console.log('错误类型:', error.name, '错误信息:', error.message)
            
            // 处理不同类型的播放错误
            if (error.name === 'NotAllowedError') {
              console.log('浏览器阻止自动播放，需要用户交互')
              // 显示用户提示
              alert('请点击播放按钮来开始播放音乐（浏览器安全策略要求）')
            }
            pause()
          })
      }
    } else {
      audio.pause()
    }
  }, [isPlaying])

  // 音量控制
  useEffect(() => {
    const audio = audioRef.current
    if (audio) {
      audio.volume = volume
    }
  }, [volume])

  // 曲目切换
  useEffect(() => {
    const audio = audioRef.current
    if (audio && track?.audioUrl) {
      console.log('加载新音频:', track.title, track.audioUrl)
      
      // 添加音频加载事件监听
      const handleLoadStart = () => console.log('开始加载音频')
      const handleCanPlay = () => console.log('音频可以播放')
      const handleError = (e) => {
        console.error('音频加载错误:', e.target.error)
        alert(`音频加载失败: ${track.title}`)
      }
      
      audio.addEventListener('loadstart', handleLoadStart)
      audio.addEventListener('canplay', handleCanPlay)
      audio.addEventListener('error', handleError)
      
      audio.src = track.audioUrl
      dispatch({ type: 'SET_CURRENT_TIME', payload: 0 })
      dispatch({ type: 'SET_DURATION', payload: 0 })
      
      // 重置播放状态，让用户手动播放
      if (isPlaying) {
        // 延迟一点时间等待音频加载
        setTimeout(() => {
          const playPromise = audio.play()
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.error('自动播放失败:', error)
              pause()
            })
          }
        }, 100)
      }
      
      // 清理事件监听器
      return () => {
        audio.removeEventListener('loadstart', handleLoadStart)
        audio.removeEventListener('canplay', handleCanPlay)
        audio.removeEventListener('error', handleError)
      }
    }
  }, [track?.audioUrl])

  const formatTime = (time) => {
    if (isNaN(time) || time === 0) return '0:00'
    
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const handleProgressClick = (e) => {
    const progressBar = progressRef.current
    if (!progressBar || !audioRef.current || !duration) return

    const rect = progressBar.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const newTime = (clickX / rect.width) * duration
    
    audioRef.current.currentTime = newTime
    dispatch({ type: 'SET_CURRENT_TIME', payload: newTime })
  }

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value)
    dispatch({ type: 'SET_VOLUME', payload: newVolume })
  }

  const toggleFavorite = () => {
    if (isFavorite(track.id)) {
      removeFavorite(track.id)
    } else {
      addFavorite(track)
    }
  }

  // 检查浏览器音频格式支持
  const checkAudioSupport = (url) => {
    const audio = new Audio()
    const extension = url.split('.').pop().toLowerCase()
    
    switch (extension) {
      case 'ogg':
        return audio.canPlayType('audio/ogg') !== ''
      case 'mp3':
        return audio.canPlayType('audio/mpeg') !== ''
      case 'wav':
        return audio.canPlayType('audio/wav') !== ''
      case 'mp4':
      case 'm4a':
        return audio.canPlayType('audio/mp4') !== ''
      default:
        return true // 假设支持
    }
  }

  // 处理播放按钮点击
  const handlePlayButtonClick = async () => {
    const audio = audioRef.current
    if (!audio) return
    
    // 如果曲目没有预览音频，给用户提示
    if (!track?.audioUrl) {
      alert(`抱歉，"${track?.title}" 没有可播放的预览音频。\n您可以点击 Spotify 链接在 Spotify 应用中收听完整版本。`)
      return
    }

    if (!isPlaying) {
      console.log('用户点击播放按钮，音频URL:', track.audioUrl)
      
      // 检查浏览器是否支持此音频格式
      if (!checkAudioSupport(track.audioUrl)) {
        console.warn('浏览器可能不支持此音频格式:', track.audioUrl)
        alert('抱歉，您的浏览器不支持此音频格式，正在尝试其他音频源...')
        // 这里可以尝试切换到下一首歌曲
        onNext()
        return
      }

      try {
        // 确保音频源已设置
        if (audio.src !== track.audioUrl) {
          console.log('设置音频源:', track.audioUrl)
          audio.src = track.audioUrl
          // 等待音频加载
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('音频加载超时')), 10000)
            audio.addEventListener('canplay', () => {
              clearTimeout(timeout)
              resolve()
            }, { once: true })
            audio.addEventListener('error', () => {
              clearTimeout(timeout)
              reject(new Error('音频加载失败'))
            }, { once: true })
            audio.load() // 强制加载
          })
        }
        
        // 直接尝试播放（用户交互后应该允许）
        await audio.play()
        play()
        console.log('播放成功')
      } catch (error) {
        console.error('播放失败:', error)
        let errorMessage = '播放失败'
        
        if (error.name === 'NotAllowedError') {
          errorMessage = '浏览器阻止音频播放，请确保已允许此网站播放音频'
        } else if (error.name === 'NotSupportedError') {
          errorMessage = '不支持的音频格式，正在尝试下一首...'
          setTimeout(() => onNext(), 1000) // 1秒后自动切换
        } else if (error.message.includes('CORS')) {
          errorMessage = '音频资源跨域问题，正在尝试下一首...'
          setTimeout(() => onNext(), 1000)
        } else if (error.message.includes('网络')) {
          errorMessage = '网络错误，请检查网络连接'
        } else {
          errorMessage = `播放失败: ${error.message}`
        }
        
        alert(errorMessage)
      }
    } else {
      // 暂停播放
      console.log('暂停播放')
      audio.pause()
      pause()
    }
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: track.title,
        text: `来听听这首歌：${track.title} - ${track.artist}`,
        url: window.location.href
      })
    } else {
      // 复制到剪贴板
      navigator.clipboard.writeText(window.location.href)
      // 可以添加一个提示
    }
  }

  if (!track) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500">没有选择曲目</p>
      </div>
    )
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto"
    >
      <audio
        ref={audioRef}
        preload="metadata"
        crossOrigin="anonymous"
        onLoadStart={() => console.log('Audio loadstart')}
        onLoadedData={() => console.log('Audio loaded data')}
        onCanPlay={() => console.log('Audio can play')}
        onError={(e) => console.error('Audio element error:', e.target.error)}
      />

      {/* 封面和信息 */}
      <div className="text-center mb-8">
        <motion.div
          animate={{ 
            rotate: isPlaying ? 360 : 0,
            scale: isPlaying ? 1.05 : 1
          }}
          transition={{ 
            rotate: { duration: 10, repeat: Infinity, ease: "linear" },
            scale: { duration: 0.3 }
          }}
          className="relative mb-6"
        >
          <img
            src={track.artwork || '/default-cover.jpg'}
            alt={track.title}
            className="w-80 h-80 rounded-2xl shadow-2xl mx-auto object-cover"
          />
          
          {/* 播放指示器 */}
          {isPlaying && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="w-20 h-20 bg-black bg-opacity-50 rounded-full flex items-center justify-center backdrop-blur-sm">
                <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
              </div>
            </motion.div>
          )}
        </motion.div>

        <h1 className="text-2xl font-bold text-gray-800 mb-2">
          {track.title}
        </h1>
        <p className="text-lg text-gray-600 mb-2">
          {track.artist}
        </p>
        
        {track.album && (
          <p className="text-sm text-gray-500 mb-2">
            专辑: {track.album}
          </p>
        )}

        {/* 演示音频提示 */}
        {/* 音频来源标识 */}
        {track.source === 'Spotify' && track.audioUrl && (
          <div className="inline-flex items-center px-3 py-1 bg-green-50 border border-green-200 rounded-full text-xs text-green-600 mb-2">
            <span className="mr-1">🎧</span>
            Spotify 预览 - 30秒片段
            {track.spotifyUrl && (
              <a 
                href={track.spotifyUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-2 text-green-700 hover:text-green-800"
                title="在 Spotify 中打开"
              >
                ↗️
              </a>
            )}
          </div>
        )}

        {track.source === 'Spotify (无预览)' || (track.source === 'Spotify' && !track.audioUrl) && (
          <div className="inline-flex items-center px-3 py-1 bg-gray-50 border border-gray-200 rounded-full text-xs text-gray-600 mb-2">
            <span className="mr-1">🎵</span>
            Spotify 音乐 - 无预览音频
            {track.spotifyUrl && (
              <a 
                href={track.spotifyUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="ml-2 text-gray-700 hover:text-gray-800"
                title="在 Spotify 中收听完整版"
              >
                🎧 收听完整版
              </a>
            )}
          </div>
        )}

        {track.source === '演示音频' && (
          <div className="inline-flex items-center px-3 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-600 mb-2">
            <span className="mr-1">🎵</span>
            演示音频 - 实际音效与标题对应
          </div>
        )}
        
        {track.source === '演示音乐' && (
          <div className="inline-flex items-center px-3 py-1 bg-green-50 border border-green-200 rounded-full text-xs text-green-600 mb-2">
            <span className="mr-1">🎶</span>
            演示音乐文件
          </div>
        )}
      </div>

      {/* 进度条 */}
      <div className="mb-6">
        <div 
          ref={progressRef}
          onClick={handleProgressClick}
          className="relative h-2 bg-gray-200 rounded-full cursor-pointer group"
        >
          <motion.div
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-blue-600 rounded-full"
            style={{ width: `${progress}%` }}
          />
          
          {/* 拖拽点 */}
          <div
            className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
            style={{ left: `${progress}%`, marginLeft: '-8px' }}
          />
        </div>
        
        <div className="flex justify-between text-sm text-gray-500 mt-2">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* 控制按钮 */}
      <div className="flex items-center justify-center space-x-6 mb-6">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsShuffled(!isShuffled)}
          className={`p-2 rounded-full ${isShuffled ? 'text-purple-600 bg-purple-100' : 'text-gray-600 hover:bg-gray-100'} transition-colors`}
        >
          <Shuffle className="w-5 h-5" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onPrevious}
          className="p-3 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        >
          <SkipBack className="w-6 h-6" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handlePlayButtonClick}
          className={`p-4 text-white rounded-full shadow-lg hover:shadow-xl transition-all ${
            !track?.audioUrl 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-gradient-to-r from-purple-500 to-blue-600'
          }`}
          title={!track?.audioUrl ? '此曲目无预览音频，点击查看 Spotify 链接' : ''}
        >
          {!track?.audioUrl ? (
            <Volume2 className="w-8 h-8" />
          ) : isPlaying ? (
            <Pause className="w-8 h-8" />
          ) : (
            <Play className="w-8 h-8 ml-1" />
          )}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onNext}
          className="p-3 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        >
          <SkipForward className="w-6 h-6" />
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setIsRepeated(!isRepeated)}
          className={`p-2 rounded-full ${isRepeated ? 'text-purple-600 bg-purple-100' : 'text-gray-600 hover:bg-gray-100'} transition-colors`}
        >
          <Repeat className="w-5 h-5" />
        </motion.button>
      </div>

      {/* 音量和额外控制 */}
      <div className="flex items-center justify-between">
        {/* 音量控制 */}
        <div className="flex items-center space-x-2 flex-1">
          <Volume2 className="w-5 h-5 text-gray-600" />
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            onChange={handleVolumeChange}
            className="flex-1 max-w-24 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        {/* 操作按钮 */}
        <div className="flex items-center space-x-2">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={toggleFavorite}
            className={`p-2 rounded-full transition-colors ${
              isFavorite(track.id) 
                ? 'text-red-500 bg-red-50' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Heart className="w-5 h-5" fill={isFavorite(track.id) ? 'currentColor' : 'none'} />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleShare}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Share className="w-5 h-5" />
          </motion.button>

          {track.downloadUrl && (
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => window.open(track.downloadUrl, '_blank')}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <Download className="w-5 h-5" />
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default MusicPlayer
